<template>
  <b-card>
    <div>

      <h2>Policy Rules</h2>

      <!-- search input -->
      <div class="custom-search d-flex align-items-center justify-content-end">
        <div
            class="d-flex flex-column flex-sm-row align-items-center mb-1 justify-content-around"
        >
          <template
              v-if="$permissionAbility(RECURRING_PAYSLIP_POLICY_CREATE, permissions)"
          >
            <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="flex-shrink-0 mr-1"
                variant="primary"
                @click="showModal"
            >
              Create
            </b-button>
          </template>
        </div>
      </div>

      <!-- table -->
      <vue-good-table
          style-class="vgt-table table-custom-style striped"
          :line-numbers="false"
          mode="remote"
          :total-rows="totalRecords"
          :is-loading.sync="isLoading"
          :rows="rows"
          :sort-options="{
          enabled: false,
          multipleColumns: true,
          initialSortBy: [
            { field: 'id', type: 'desc' },
            { field: 'created_at', type: 'desc' },
          ],
        }"
          :columns="columns"
          :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-column-filter="onColumnFilter"
          @on-per-page-change="onPerPageChange"
      >
        <template
            slot="table-row"
            slot-scope="props"
        >
          <!-- Column: Action -->

          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body align-middle mr-25"
                  />
                </template>
                <template
                    v-if="$permissionAbility(RECURRING_PAYSLIP_POLICY_EDIT, permissions)"
                >
                  <b-dropdown-item @click="onShow(props.row)">
                    <feather-icon
                        icon="Edit2Icon"
                        class="mr-50"
                    />
                    <span>Edit</span>
                  </b-dropdown-item>
                </template>

                <template
                    v-if="
                    $permissionAbility(RECURRING_PAYSLIP_POLICY_DELETE, permissions)
                  "
                >
                  <b-dropdown-item @click="onDelete(props.row.id)">
                    <feather-icon
                        icon="TrashIcon"
                        class="mr-50"
                    />
                    <span>Delete</span>
                  </b-dropdown-item>
                </template>
              </b-dropdown>
            </span>
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template
            slot="pagination-bottom"
            slot-scope="props"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                  v-model="pageLength"
                  :options="['10', '25', '50', '100', '500']"
                  class="mx-1"
                  @input="
                  (value) => props.perPageChanged({ currentPerPage: value })
                "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

    <b-modal
        id="modal-recurring-policy-form"
        centered
        :title="
        modelType == 'editModel'
          ? 'Edit Policy Rule'
          : 'Create Policy Rule'
      "
        hide-footer
        no-close-on-backdrop
        @hidden="hiddenModal"
    >

      <validation-observer ref="policyForm">
        <b-form @submit.prevent="submit">

          <p class="d-flex flex-wrap align-items-center">
            In every &nbsp;
            <validation-provider
                #default="{ errors }"
                name="cycle"
                vid="cycle"
                rules="required|numeric|min:1|max:255"
            >
    <span class="d-inline-block mr-1" style="width: 100px;">
      <b-form-input
          v-model="cycle"
          type="number"
          :state="errors.length ? false : null"
          placeholder="Cycle"
          style="width: 100%;"
      />
      <div style="min-height: 1em;">
        <small class="text-danger">{{ errors[0] }}</small>
      </div>
    </span>
            </validation-provider>
            &nbsp;day<span v-if="cycle > 1">s</span>, you must complete at least&nbsp;

            <validation-provider
                #default="{ errors }"
                name="min hour"
                vid="min_hour"
                rules="required|numeric|min:1"
            >
    <span class="d-inline-block mr-1" style="width: 100px;">
      <b-form-input
          v-model="min_hour"
          type="number"
          :state="errors.length ? false : null"
          placeholder="Min Hour"
          style="width: 100%;"
      />
      <div style="min-height: 1em;">
        <small class="text-danger">{{ errors[0] }}</small>
      </div>
    </span>
            </validation-provider>
            &nbsp;hour<span v-if="min_hour > 1">s</span> and at most &nbsp;

            <validation-provider
                #default="{ errors }"
                name="max hour"
                vid="max_hour"
                rules="required|numeric|min:1"
            >
    <span class="d-inline-block mr-1" style="width: 100px;">
      <b-form-input
          v-model="max_hour"
          type="number"
          :state="errors.length ? false : null"
          placeholder="Max Hour"
          style="width: 100%;"
      />
      <div style="min-height: 1em;">
        <small class="text-danger">{{ errors[0] }}</small>
      </div>
    </span>
            </validation-provider>
            &nbsp;hour<span v-if="max_hour > 1">s</span>.
          </p>



          <!-- loading button -->
          <template v-if="isLoading">
            <b-button
                class="float-right"
                variant="primary"
                disabled
            >
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                class="float-right"
                variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>

  </b-card>
</template>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BForm,
  BModal,
  BSpinner,
  BFormDatepicker,
  VBTooltip, BTable, BRow, BCol,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, max } from '@validations'
import { salaryTypeConstants } from '@/helpers/constant/salaryTypeConstant'
import { lateTypeConstants } from '@/helpers/constant/lateTypeConstant'
import { amountValueTypeConstants } from '@/helpers/constant/amountValueTypeConstant'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'
import {
  RECURRING_PAYSLIP_POLICY_CREATE,
  RECURRING_PAYSLIP_POLICY_EDIT,
  RECURRING_PAYSLIP_POLICY_DELETE,
  RECURRING_PAYSLIP_POLICY_SHOW,
} from '@/helpers/permissionsConstant'

export default {
  name: 'PayslipHourlyPolicyRules',
  components: {
    BCol,
    BRow,
    BTable,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BFormDatepicker,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: ['policy'],
  data() {
    return {
      RECURRING_PAYSLIP_POLICY_CREATE,
      RECURRING_PAYSLIP_POLICY_EDIT,
      RECURRING_PAYSLIP_POLICY_DELETE,
      RECURRING_PAYSLIP_POLICY_SHOW,

      isLoading: false,
      modelType: '',
      policyId: '',

      salaryTypeOptions: [
        { value: 'monthly', name: 'Monthly' },
        // { value: 'hourly', name: 'Hourly' },
      ],
      typeOptions: [
        { value: 'monthly', name: 'Monthly' },
        { value: 'custom', name: 'Custom' },
      ],

      cycle: '',
      max_hour: null,
      min_hour: null,

      pageLength: 10,
      columns: [
        {
          label: 'Cycle (in days)',
          field: 'cycle_in_days',
          sortable: false,
        },
        {
          label: 'Max Hour',
          field: 'max_hours',
          sortable: false,
        },
        {
          label: 'Min Hour',
          field: 'min_hours',
          sortable: false,
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: '',
      delayTimer: null,
      isLoading: false,
      isDelayFinePolicy: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [
          { field: 'id', type: 'desc' },
          { field: 'created_at', type: 'desc' },
        ],
        page: 1,
        perPage: 10,
      },
    }
  },

  computed: {
    ...mapGetters({
      permissions: 'userModule/getPermissions',
    }),
  },
  methods: {

    onShowPage(value)
    {
      this.$router.push({
        name: 'admin-payslip-recurring-policy-assign-details',
        params: {
          id: value?.id,
        },
      })
    },
    onContextPurchaseDate(ctx) {
      this.purchaseDate = ctx.selectedYMD
    },
    showModal() {
      this.$bvModal.show('modal-recurring-policy-form')
    },
    hiddenModal() {
      this.modelType = ''
      this.$bvModal.hide('modal-recurring-policy-form')
      this.resetModal()
    },
    resetModal() {
      this.delayFinePolicyId = ''
      this.cycle = ''

      this.max_hour = null
      this.min_hour = null
    },
    async onShow(value) {
      this.modelType = 'editModel'
      this.policyId = value?.id

      this.cycle = value?.cycle_in_days
      this.max_hour = value?.max_hours
      this.min_hour = value?.min_hours
      this.showModal()
    },
    async onDelete(id) {
      this.$swal({
        title: 'Warning!',
        text: 'Are You Sure You Want To Delete This?',
        icon: 'warning',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: 'Ok',
        showLoaderOnConfirm: true,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`/api/hourly-policy-rules/${id}`)

            this.loadItems()

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'BellIcon',
                variant: 'success',
                text: 'Successfully Deleted',
              },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'BellIcon',
                variant: 'danger',
                text: error?.response?.data?.message,
              },
            })
          }
        }
      })
    },

    formatAmount(row) {
      if (row?.amount_value_type == 'percentage') {
        return `${row?.amount_value} %`
      }
      return row?.amount_value
    },
    formatAmountType(value) {
      const amountType = amountValueTypeConstants.find(
          item => item.value === value,
      )
      return amountType?.name
    },
    formatLateType(value) {
      const lateType = lateTypeConstants.find(
          item => item.value === value,
      )
      return lateType?.name
    },

    formatSalaryType(value) {
      const salaryType = salaryTypeConstants.find(
          item => item.value === value,
      )
      return salaryType?.name
    },
    formatDate(value) {
      if (value) {
        return this.$moment(value).format('MMM Do YYYY')
      }
      return 'Not Set'
    },
    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer)
        this.delayTimer = null
      }

      this.delayTimer = setTimeout(() => {
        this.loadItems()
      }, 1000)
    },

    updateParams(newProps) {
      this.serverParams = { ...this.serverParams, ...newProps }
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage })
      this.loadItems()
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage })
      this.loadItems()
    },

    onSortChange(params) {
      this.updateParams({
        sort: params,
      })
      this.loadItems()
    },

    onColumnFilter(params) {
      this.updateParams(params)
      this.loadItems()
    },

    async getData(params) {
      return await this.$api.get('api/hourly-policy-rules/' + this.policy, {
        params: {
          show: params.show,
          page: params.page,
          sort: params.sort,
          q: params.q,
        },
      })
    },

    async loadItems() {
      try {
        const response = await this.getData({
          show: this.serverParams.perPage,
          page: this.serverParams.page,
          sort: this.serverParams.sort,
          q: this.searchTerm,
        })

        const data = response?.data?.data
        const meta = response?.data?.meta

        this.totalRecords = meta?.pagination?.total
        this.rows = data
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'BellIcon',
            variant: 'warning',
            text: error?.response?.data?.message,
          },
        })
      }
    },
    async submit() {
      this.$refs.policyForm.validate().then(async success => {
        if (success) {
          this.isLoading = true
          try {
            const policyData = {
              hourly_work_policy_d: this.policy,
              cycle_in_days: this.cycle,
              max_hours: this.max_hour,
              min_hours: this.min_hour,
            }

            if (this.modelType === 'editModel') {
              const response = await this.$api.put(`/api/hourly-policy-rules/${this.policyId}`, policyData)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Success',
                  icon: 'BellIcon',
                  variant: 'success',
                  text: 'Recurring Policy Successfully Updated',
                },
              })
            } else {
              const response = await this.$api.post(`/api/hourly-policy-rules`, policyData)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Success',
                  icon: 'BellIcon',
                  variant: 'success',
                  text: 'Recurring Policy Successfully Created',
                },
              })
            }

            this.loadItems() // Refresh item list
            this.hiddenModal() // Close the modal
            this.isLoading = false
          } catch (error) {
            this.isLoading = false
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Error',
                  icon: 'BellIcon',
                  variant: 'danger',
                  text: error?.response?.data?.message,
                },
              })
            }

            if (error?.response?.data?.errors) {
              this.$refs.addDelayFinePolicy.setErrors(error?.response?.data?.errors)
            }
          }
        }
      })
    },

  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.required-label label::after {
  content: " *";
  color: red;
}
</style>
